// Analytics Service (F-205 / T-176-001..003 — batch 4 S6-S8).
//
// GA4 functions (initGA / logPageView / logEvent / logSignUp / logLogin /
// logSubscription / logTrialStart / logPhoneVerification / logPropertyAdded /
// logDocumentUpload / logFeedbackSubmitted) are preserved unchanged so the
// existing wiring (App.tsx initGA, PageViewTracker logPageView) keeps working.
//
// On top of GA4 this module adds a Mixpanel-backed facade:
//   - six core product events (signup, service purchased, commitment started,
//     upgraded, enterprise inquiry, churn) via trackSignup / ... helpers
//   - identify / group user+plan properties (T-176-002)
//   - a cookie-consent gate + opt-out control (T-176-003)
// All sending is gated on consent; in dev without a Mixpanel token the events
// still "fire" (they log to the console) so development builds observe the
// funnel without an SDK key.

// Static import (batch-4 shape). F-184 swapped this for `new Function('require')`,
// which bypassed Jest's registry (5 tests red) and resolved nothing in the
// browser — mixpanel-browser was never bundled, so every send hit the no-op.
import mixpanel from 'mixpanel-browser';

export const GA_MEASUREMENT_ID = 'G-HCFGKJG5KM'; // Google Analytics 4 Measurement ID

export const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';

export const ANALYTICS_CONSENT_KEY = 'lp_analytics_consent';
export type AnalyticsConsent = 'granted' | 'denied' | null;

// —— Consent gate (T-176-003) ——————————————————————————————————————————————

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const raw = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return raw === 'granted' || raw === 'denied' ? raw : null;
  } catch {
    return null;
  }
}

export function setAnalyticsConsent(granted: boolean): void {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch {
    /* ignore storage errors */
  }
  if (!granted) {
    optOutAnalytics();
  } else if (mixpanelInitialized && typeof mixpanel.opt_in_tracking === 'function') {
    try {
      mixpanel.opt_in_tracking();
    } catch {
      /* ignore */
    }
  }
}

export function isConsentGranted(): boolean {
  return getAnalyticsConsent() === 'granted';
}

/** Full opt-out: blocks future sends and clears Mixpanel's persisted profile key. */
export function optOutAnalytics(): void {
  setConsentDenied();
  if (typeof mixpanel?.opt_out_tracking === 'function') {
    try {
      mixpanel.opt_out_tracking();
    } catch {
      /* ignore */
    }
  }
}

function setConsentDenied(): void {
  try {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, 'denied');
  } catch {
    /* ignore */
  }
}

// —— Mixpanel lifecycle (T-176-001) ———————————————————————————————————————

let mixpanelInitialized = false;

export function initAnalytics(): void {
  if (typeof window === 'undefined' || !MIXPANEL_TOKEN) {
    return;
  }
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV !== 'production',
      track_pageview: false,
    });
    mixpanelInitialized = true;
    console.log('[Analytics] Mixpanel initialized');
  } catch {
    /* none — GA4 carries the page view instead */
  }
}

function safeTrack(event: string, props?: Record<string, any>): void {
  if (!isConsentGranted()) {
    return;
  }
  if (mixpanelInitialized) {
    try {
      mixpanel.track(event, props);
      return;
    } catch {
      /* fall through to dev log */
    }
  }
  // Events "fire in dev": with no SDK token we still surface the funnel.
  console.log('[Analytics]', event, props ?? {});
}

// —— Six core product events (T-176-001) ——————————————————————————————————

export const ANALYTICS_EVENTS = {
  SIGNUP: 'signup',
  SERVICE_PURCHASED: 'service_purchased',
  COMMITMENT_STARTED: 'commitment_started',
  UPGRADED: 'upgraded',
  ENTERPRISE_INQUIRY: 'enterprise_inquiry',
  CHURN: 'churn',
} as const;
export type AnalyticsCoreEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Any of the six core events, tagged with an arbitrary property bag. */
export const trackCore = (event: AnalyticsCoreEvent, props?: Record<string, any>): void =>
  safeTrack(event, props);

export const trackSignup = (method: string): void =>
  safeTrack(ANALYTICS_EVENTS.SIGNUP, { method });

export const trackLogin = (method: string): void =>
  safeTrack('login', { method });

export const trackCommitmentStarted = (props?: Record<string, any>): void =>
  safeTrack(ANALYTICS_EVENTS.COMMITMENT_STARTED, props);

export const trackServicePurchased = (props?: Record<string, any>): void =>
  safeTrack(ANALYTICS_EVENTS.SERVICE_PURCHASED, props);

export const trackUpgraded = (props?: Record<string, any>): void =>
  safeTrack(ANALYTICS_EVENTS.UPGRADED, props);

export const trackEnterpriseInquiry = (props?: Record<string, any>): void =>
  safeTrack(ANALYTICS_EVENTS.ENTERPRISE_INQUIRY, props);

export const trackChurn = (props?: Record<string, any>): void =>
  safeTrack(ANALYTICS_EVENTS.CHURN, props);

// —— Identify / group user+plan properties (T-176-002) ——————————————————————

export interface AnalyticsUserIdentity {
  distinctId: string;
  email?: string;
  name?: string;
  plan?: string | null;
  tier?: string | null;
  signupDate?: string | number | null;
  role?: string;
}

export function trackIdentify(user: AnalyticsUserIdentity): void {
  if (!isConsentGranted() || !user?.distinctId) return;
  if (mixpanelInitialized) {
    try {
      mixpanel.identify(user.distinctId);
      const props: Record<string, any> = {};
      if (user.email) props.$email = user.email;
      if (user.name) props.$name = user.name;
      if (user.plan) props.plan = String(user.plan);
      if (user.tier) props.tier = String(user.tier);
      if (user.signupDate) props.signupDate = user.signupDate;
      if (user.role) props.role = user.role;
      mixpanel.people.set(props as any);
      return;
    } catch {
      /* fall through */
    }
  }
  console.log('[Analytics] identify', user.distinctId, user);
}

/** Refresh plan-level group properties when the tier changes (T-176-002). */
export function trackGroup(plan?: { plan?: string | null; tier?: string | null; status?: string | null }): void {
  if (!isConsentGranted()) return;
  const tier = plan?.tier || plan?.plan;
  if (mixpanelInitialized && tier) {
    try {
      // mixpanel-browser ships `group` at runtime but its typings omit it,
      // so call it through a narrow, guarded cast (same pattern as opt-out).
      const mixpanelGroup = (mixpanel as unknown as { group: (key: string, value: string) => void })
        ?.group;
      if (typeof mixpanelGroup === 'function') {
        mixpanelGroup('plan', String(tier));
        mixpanel.people.set({ plan: String(tier) } as any);
        return;
      }
    } catch {
      /* fall through */
    }
  }
  console.log('[Analytics] group(plan)', plan);
}

/**
 * Detect a churn transition purely: previous snapshot was an active/trial
 * account and the fresh snapshot is cancelled/inactive. Kept as a pure helper
 * so the churn trigger is unit-testable without touching Mixpanel.
 */
export function detectChurnTransition(
  prev?: { tier?: string | null; status?: string | null } | null,
  next?: { tier?: string | null; status?: string | null } | null
): boolean {
  if (!prev || !next) return false;
  const wasActive =
    prev.status === 'active' || prev.status === 'trial' || prev.status == null;
  const nowInactive =
    next.status === 'cancelled' || next.status === 'inactive' || next.status === 'expired';
  return wasActive && nowInactive;
}

// —— Experiment variant-tagged events (T-164-002) ——————————————————————————

/** Completion/booking events carry the assigned variant so the milestone can
 *  be bucketed per experiment arm for the significance calculator (S9). */
export function trackVariantEvent(eventName: string, props?: Record<string, any>): void {
  safeTrack(eventName, props);
}

// GA4 (compatibility, unchanged) ————————————————————————————————————————————

// Initialize Google Analytics
export const initGA = () => {
    if (typeof window !== 'undefined' && !window.gtag) {
        // Load gtag script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_path: window.location.pathname,
        });

        console.log('[GA] Google Analytics initialized');
    }
};

// Track page view
export const logPageView = (path: string) => {
    if (window.gtag) {
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_path: path,
        });
        console.log('[GA] Page view:', path);
    }
};

// Track custom event
export const logEvent = (eventName: string, eventParams?: Record<string, any>) => {
    if (window.gtag) {
        window.gtag('event', eventName, eventParams);
        console.log('[GA] Event:', eventName, eventParams);
    }
};

// Track sign up
export const logSignUp = (method: string) => {
    logEvent('sign_up', {
        method: method,
    });
};

// Track login
export const logLogin = (method: string) => {
    logEvent('login', {
        method: method,
    });
};

// Track property added
export const logPropertyAdded = (type: 'single' | 'bulk', count: number = 1) => {
    logEvent('add_property', {
        property_type: type,
        property_count: count,
    });
};

// Track document upload
export const logDocumentUpload = (documentType: string) => {
    logEvent('upload_document', {
        document_type: documentType,
    });
};

// Track feedback submission
export const logFeedbackSubmitted = (category: string) => {
    logEvent('submit_feedback', {
        feedback_category: category,
    });
};

// Track subscription
export const logSubscription = (plan: string, value: number) => {
    logEvent('purchase', {
        transaction_id: Date.now().toString(),
        value: value,
        currency: 'GBP',
        items: [{
            item_id: plan,
            item_name: `Landlord Pro - ${plan}`,
            price: value,
        }],
    });
};

// Track trial start
export const logTrialStart = () => {
    logEvent('begin_trial', {
        trial_duration: 14,
    });
};

// Track phone verification
export const logPhoneVerification = () => {
    logEvent('phone_verified', {});
};

// Declare gtag types for TypeScript
declare global {
    interface Window {
        gtag: (...args: any[]) => void;
        dataLayer: any[];
    }
}